'use client';

import { useEffect, useRef, useState } from 'react';
import { createContentCount, getConfig, noToShow, sortItems } from './utils';
import { ListHeader } from './ListHeader';
import { List } from './List';
import { cn, IDynamicListConfig, IDynamicListContentCount, IDynamicListProps } from 'types';

export const DynamicList = <TData extends Record<string, any>>(props: IDynamicListProps<TData>) => {
  const { EmptyComponent, LoadMoreComponent, className = '', headerClassName = '', rowClassName = '' } = props;
  const [config, setConfig] = useState<IDynamicListConfig>(getConfig(props as IDynamicListProps<Record<string, any>>));
  const [contentCount, setContentCount] = useState<IDynamicListContentCount>({
    totalNumber: 0,
    showingNumber: 0,
  });

  const [sortKey, setSortKey] = useState(props.sort?.map((x) => x?.key) ?? []);
  const [sortReverse, setSortReverse] = useState(props.sort?.map((x) => x?.reverse) ?? []);
  const [sorted, setSorted] = useState<TData[]>([]);
  const [toShow, setToShow] = useState<TData[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>(props.selectedRows ?? []);
  const [loadingMore, setLoadingMore] = useState(props.loading);
  const [listScrollDown, setListScrollDown] = useState(false);
  const scrollElement = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!props.pagination) return;
    const showCount = noToShow(props.pagination);
    setHasMore(props.pagination?.totalItems > showCount);
    setToShow([...sorted].splice(0, showCount));
    setContentCount(createContentCount(sorted.length, props.pagination));
  }, [props.pagination]);

  // Setting the current value of "sort" to reflect recently changed value
  useEffect(() => {
    setSortKey(props.sort?.map((x) => x?.key));
  }, [props.sort]);

  useEffect(() => {
    setConfig(getConfig(props as IDynamicListProps<Record<string, any>>));
  }, [props.data, props.config]);

  useEffect(() => {
    const sortedData = sortItems(
      props.data,
      sortKey?.map((x, index) => ({ key: x, reverse: sortReverse?.[index] }))
    ).sort((a, b) => (a?.disabled ?? false) - (b?.disabled ?? false));
    setSorted(sortedData);
    if (props.pagination) {
      const showCount = noToShow(props.pagination);
      setToShow([...sortedData].splice(0, showCount));
      setContentCount(createContentCount(sortedData.length, props.pagination));
    } else {
      setToShow(sortedData);
    }

    setLoadingMore(props.loading);
  }, [props.data, sortKey, sortReverse]);

  useEffect(() => {
    setSelectedRows(props.selectedRows ?? []);
  }, [props.selectedRows]);

  useEffect(() => {
    setLoadingMore(props.loading);
  }, [props.loading]);

  // notify parent about visible rows whenever toShow changes
  useEffect(() => {
    if (typeof props.onVisibleDataChange === 'function') {
      props.onVisibleDataChange(toShow);
    }
  }, [toShow]);

  useEffect(() => {
    if (listScrollDown) {
      scrollElement.current.scrollTop = scrollElement?.current?.scrollHeight;
    }
  }, [toShow?.length]);

  const onSortChange = (value: string) => {
    const allowedSortKeys = props.config?.cols?.filter((x) => !x.noSort)?.map((x) => x?.key);
    if (allowedSortKeys.includes(value)) {
      if (value === sortKey[0]) {
        setSortReverse([!sortReverse[0]]);
      } else {
        setSortKey([value]);
        setSortReverse([false]);
      }
    }
  };

  const onLoadMore = async () => {
    setListScrollDown(true);
    if (props.onLoadMore) {
      setLoadingMore(true);
      await props.onLoadMore();
    }

    setLoadingMore(props.loading);
  };

  const onSelectRow = (data: any) => {
    if (typeof config.maxSelected === 'number') {
      let selected = [...selectedRows];
      const index = selected.findIndex((row) => row === data);
      if (index === -1) {
        if (config.maxSelected === 1) {
          selected = [data];
        } else if (selectedRows.length < config.maxSelected) {
          selected = [...selectedRows, data];
        }
      } else {
        if (config.maxSelected === 1) {
          selected = [];
        } else {
          selected.splice(index, 1);
        }
      }
      setSelectedRows(selected);
      if (typeof props.onSelectedRows === 'function') {
        props.onSelectedRows(selected);
      }
    }
  };

  const onElementClick = (data: any) => {
    if (config.selectable) {
      onSelectRow(data);
    }
    if (typeof props.onElementClick === 'function') {
      props.onElementClick(data);
    }
  };

  const gridCols = config?.cols.map((col) => `${col.colWidth}`).join(' ');

  return (
    <div className={cn('border border-neutralGrey w-full max-w-full bg-lightGrey rounded-lg', className)}>
      <div
        className="grid auto-rows-auto [grid-template-columns:var(--grid-cols)]"
        style={{ '--grid-cols': gridCols } as React.CSSProperties}
        ref={scrollElement}
      >
        <ListHeader
          sort={sortKey?.map((x, index) => ({ key: x, reverse: sortReverse?.[index] }))}
          config={config}
          onSortChange={onSortChange}
          className={headerClassName}
        />
        {toShow?.length > 0 && (
          <List
            data={toShow}
            config={config}
            onElementClick={onElementClick}
            accordionChild={props.accordionChild}
            className={rowClassName}
          />
        )}
      </div>
      {toShow?.length == 0 && EmptyComponent && (
        <EmptyComponent onEmptyElementClick={props?.onEmptyElementClick} isLoading={loadingMore} />
      )}
      {props.onLoadMore && hasMore && LoadMoreComponent && (
        <LoadMoreComponent loadingMore={loadingMore} onLoadMore={onLoadMore} />
      )}
    </div>
  );
};
